import { apiFetch } from "./apiClient";

export interface ConfigOption {
  id: number;
  categoria: string;
  valor: string;
  descripcion?: string | null;
  orden: number;
  activo: boolean;
}

export interface ImageOptimizationSettings {
  maxSide: number;
  minSide: number;
  quality: number;
  targetKB: number;
  format: "auto" | "jpeg" | "webp";
}

export interface ImageOptimizationResponse {
  data: ImageOptimizationSettings;
  defaults: ImageOptimizationSettings;
}

/** Calidad con la que arranca el reproductor: `baja` = versión liviana, `alta` = original. */
export type CalidadReproductor = "baja" | "alta";

/** Rango que acepta el backend para la altura de la versión liviana (px). */
export const ALTURA_PREVIEW_MIN = 240;
export const ALTURA_PREVIEW_MAX = 720;

export interface VideoOptimizationSettings {
  enabled: boolean;
  format: "mp4" | "webm";
  codec: "h264" | "h265" | "vp9";
  /** Resolución máxima en px del LADO MÁS CORTO del video («720p»): 240..4320. El nombre es histórico. */
  maxHeight: number;
  fps: number;
  crf: number;
  audioBitrate: number;
  preset:
    | "ultrafast"
    | "superfast"
    | "veryfast"
    | "faster"
    | "fast"
    | "medium"
    | "slow"
    | "slower"
    | "veryslow";
  /** Genera la versión liviana de cada video (requiere `enabled`). */
  previewEnabled: boolean;
  /** Lado más corto (px) de la versión liviana: 240..720 (480 = 854x480 en horizontal, 480x854 en vertical). */
  previewHeight: number;
  /** Calidad con la que se abre el reproductor cuando la persona no eligió una. */
  playerDefaultQuality: CalidadReproductor;
}

/**
 * Defaults de la pestaña. Coinciden con `VIDEO_OPT_DEFAULTS` del backend
 * (`videoOptimization.service.js`); el servidor manda los suyos en `defaults`, esto
 * solo cubre un servidor que todavía no los envía todos.
 */
export const VIDEO_OPT_DEFAULTS: VideoOptimizationSettings = {
  enabled: true,
  format: "mp4",
  codec: "h264",
  maxHeight: 720,
  fps: 30,
  crf: 23,
  audioBitrate: 128,
  preset: "medium",
  previewEnabled: true,
  previewHeight: 480,
  playerDefaultQuality: "baja",
};

export interface VideoOptimizationResponse {
  data: VideoOptimizationSettings;
  defaults: VideoOptimizationSettings;
}

/**
 * Lo que recibe un usuario que NO es administrador: el servidor le manda solo los tres ajustes
 * que necesita el reproductor (mínimo privilegio). Los demás parámetros de ffmpeg son del admin.
 */
export type VideoPlayerSettings = Pick<
  VideoOptimizationSettings,
  "previewEnabled" | "previewHeight" | "playerDefaultQuality"
>;

/**
 * Qué códecs admite cada formato (espejo de `CODECS_POR_FORMATO` en el backend,
 * `utils/videoFormat.js`): WebM solo admite VP9; MP4 admite los tres. ffmpeg no puede guardar
 * H.264/H.265 en WebM, así que el servidor rechaza esa combinación.
 */
export const CODECS_POR_FORMATO: Record<
  VideoOptimizationSettings["format"],
  VideoOptimizationSettings["codec"][]
> = {
  mp4: ["h264", "h265", "vp9"],
  webm: ["vp9"],
};

export interface CombinacionVideo {
  format: VideoOptimizationSettings["format"];
  codec: VideoOptimizationSettings["codec"];
}

/**
 * Lleva el par formato/códec a una combinación válida. `cambio` dice qué eligió la persona
 * (o `"codec"` al cargar algo ya guardado: gana el códec, igual que en el servidor):
 *   - eligió el FORMATO webm con un códec que no cabe  → el códec pasa a VP9;
 *   - eligió el CÓDEC H.264/H.265 estando en webm      → el formato vuelve a MP4.
 * `aviso` explica el ajuste en una frase corta (null si no hubo ninguno).
 */
export function corregirFormatoCodec(
  valores: CombinacionVideo,
  cambio: "format" | "codec",
): CombinacionVideo & { aviso: string | null } {
  if (CODECS_POR_FORMATO[valores.format]?.includes(valores.codec)) {
    return { ...valores, aviso: null };
  }
  if (cambio === "format") {
    return {
      format: valores.format,
      codec: "vp9",
      aviso: "WebM solo admite el códec VP9: el códec pasó a VP9.",
    };
  }
  const nombre = valores.codec === "h265" ? "H.265" : "H.264";
  return {
    format: "mp4",
    codec: valores.codec,
    aviso: `${nombre} no se puede guardar en WebM: el formato pasó a MP4.`,
  };
}

/**
 * Colores universales del calendario (definidos en backend, comunes a todos
 * los usuarios). Cada clave mapea a un tipo de evento; el valor es un hex
 * `#RGB` / `#RRGGBB` / `#RRGGBBAA`. Se inyectan como CSS vars `--cal-color-*`
 * desde `useCalendarColors`. Ver docs/modulo-calendario.md.
 */
export interface CalendarColorsSettings {
  booking: string;
  cleaning: string;
  blocked_admin: string;
  blocked_client: string;
  maintenance_admin: string;
  maintenance_client: string;
}

export interface CalendarColorsResponse {
  data: CalendarColorsSettings;
  defaults: CalendarColorsSettings;
}

/**
 * Ajustes generales del sistema (singleton):
 *  - umbralPrecioBajo: monto CLP por debajo del cual fijar un precio por noche
 *    base exige una segunda confirmación (red anti-error de tipeo). Genérico
 *    para toda propiedad. Ver docs/modulo-calendario.md (guardas de precio).
 *  - ivaPorcentaje: tasa de IVA (%) usada por TODO cálculo de IVA del sistema
 *    (round 2026-07-03). Default 19. Frontend: utils/iva.ts; backend:
 *    utils/ivaRate.js. Ver docs/flujo-financiero.md §8.
 *  - correosNotificacionActivos / correoCierreMensual / correoTareaCompartida:
 *    interruptores de los correos de AVISO al cliente (round 2026-07-28). El
 *    primero es el maestro. Solo gobiernan avisos — la recuperación de
 *    contraseña y el correo de bienvenida NUNCA pasan por acá. Todos default
 *    true. Backend: utils/notificacionesCorreo.js.
 */
export interface GeneralSettings {
  umbralPrecioBajo: number;
  ivaPorcentaje?: number;
  /** Días hacia adelante que ve y puede tomar una aseadora de un aseo abierto (1..90, default 14). */
  ventanaAseosAbiertosDias?: number;
  correosNotificacionActivos?: boolean;
  correoCierreMensual?: boolean;
  correoTareaCompartida?: boolean;
  /**
   * ¿El asistente de WhatsApp guarda gastos DE VERDAD? (round 2026-10-30, etapa B3.) APAGADO por defecto: apagado, todo
   * lo que entra por WhatsApp es una simulación. Solo lo reciben admin y superadmin, y SOLO el superadmin puede
   * cambiarlo (encender exige además `confirmacion`). No lo manda ninguna otra pestaña: se cambia únicamente desde
   * «Asistente de WhatsApp» (services/botAsistente.api.ts).
   */
  botModoReal?: boolean;
}

export interface GeneralSettingsResponse {
  data: GeneralSettings;
  defaults: GeneralSettings;
}

export const configOptionApi = {
  /** GET /config-options/:categoria — active options only */
  list: (categoria: string): Promise<{ data: ConfigOption[] } | null> =>
    apiFetch<{ data: ConfigOption[] }>(`/config-options/${categoria}`, {
      errorMsg: `Error al cargar opciones de ${categoria}`,
    }),

  /** GET /config-options/:categoria/all — all including inactive (admin) */
  listAll: (categoria: string): Promise<{ data: ConfigOption[] } | null> =>
    apiFetch<{ data: ConfigOption[] }>(`/config-options/${categoria}/all`, {
      errorMsg: `Error al cargar opciones de ${categoria}`,
    }),

  /** POST /config-options */
  create: (
    body: { categoria: string; valor: string; orden?: number },
  ): Promise<{ data: ConfigOption } | null> =>
    apiFetch<{ data: ConfigOption }>("/config-options", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Opción creada",
      errorMsg: "Error al crear opción",
    }),

  /** PUT /config-options/:id
   *  `propagated` = cuántas fichas de usuario quedaron actualizadas al
   *  renombrar un banco / tipo de cuenta (0 para otras categorías). */
  update: (
    id: number,
    body: Partial<{ valor: string; orden: number; activo: boolean; descripcion: string }>,
  ): Promise<{ data: ConfigOption; propagated?: number } | null> =>
    apiFetch<{ data: ConfigOption; propagated?: number }>(`/config-options/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Opción actualizada",
      errorMsg: "Error al actualizar opción",
    }),

  /** DELETE /config-options/:id (soft delete) */
  delete: (id: number): Promise<any | null> =>
    apiFetch(`/config-options/${id}`, {
      method: "DELETE",
      successMsg: "Opción desactivada",
      errorMsg: "Error al desactivar opción",
    }),

  /** GET /config-options/:id/usage — cuántas entidades usan este valor.
   *  `entity` rotula el mensaje en la UI: "propiedad" (plan) o "cliente"
   *  (banco / tipo de cuenta). */
  usage: (
    id: number,
  ): Promise<{
    count: number;
    refColumn: string | null;
    entity: "propiedad" | "cliente" | null;
  } | null> =>
    apiFetch(`/config-options/${id}/usage`, {
      errorMsg: "Error al verificar uso de la opción",
    }),

  /** DELETE /config-options/:id/hard?confirm=true — eliminación definitiva.
   *  Setea a NULL la referencia en propiedades (para `plan`) antes de borrar.
   *  Retorna cuántas propiedades quedaron sin plan. */
  hardDelete: (
    id: number,
  ): Promise<{
    message: string;
    clearedProperties: number;
    refColumn: string | null;
  } | null> =>
    apiFetch(`/config-options/${id}/hard?confirm=true`, {
      method: "DELETE",
      successMsg: "Opción eliminada",
      errorMsg: "Error al eliminar la opción",
    }),

  /** POST /config-options/seed — seed defaults */
  seed: (): Promise<any | null> =>
    apiFetch("/config-options/seed", {
      method: "POST",
      errorMsg: "Error al inicializar opciones",
    }),

  /** GET /config-options/image-optimization — lee settings singleton. */
  getImageOptimization: (): Promise<ImageOptimizationResponse | null> =>
    apiFetch<ImageOptimizationResponse>("/config-options/image-optimization", {
      errorMsg: "Error al cargar configuración de imágenes",
    }),

  /** PUT /config-options/image-optimization — admin only. */
  updateImageOptimization: (
    body: Partial<ImageOptimizationSettings>,
  ): Promise<ImageOptimizationResponse | null> =>
    apiFetch<ImageOptimizationResponse>("/config-options/image-optimization", {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Configuración guardada",
      errorMsg: "Error al guardar la configuración",
    }),

  /** GET /config-options/video-optimization — lee settings singleton. El administrador recibe
   *  todos los ajustes; el resto de los roles, solo los de `VideoPlayerSettings` (mínimo
   *  privilegio), por eso quien no es admin no debe asumir el resto de los campos. */
  getVideoOptimization: (): Promise<VideoOptimizationResponse | null> =>
    apiFetch<VideoOptimizationResponse>("/config-options/video-optimization", {
      errorMsg: "Error al cargar configuración de videos",
    }),

  /** PUT /config-options/video-optimization — admin only. */
  updateVideoOptimization: (
    body: Partial<VideoOptimizationSettings>,
  ): Promise<VideoOptimizationResponse | null> =>
    apiFetch<VideoOptimizationResponse>("/config-options/video-optimization", {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Configuración de videos guardada",
      errorMsg: "Error al guardar la configuración de videos",
    }),

  /** GET /config-options/calendar-colors — lectura abierta a cualquier
   *  autenticado. Lo consume `useCalendarColors` al montar el calendario. */
  getCalendarColors: (): Promise<CalendarColorsResponse | null> =>
    apiFetch<CalendarColorsResponse>("/config-options/calendar-colors", {
      errorMsg: "Error al cargar colores del calendario",
    }),

  /** PUT /config-options/calendar-colors — admin only. */
  updateCalendarColors: (
    body: Partial<CalendarColorsSettings>,
  ): Promise<CalendarColorsResponse | null> =>
    apiFetch<CalendarColorsResponse>("/config-options/calendar-colors", {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Colores del calendario guardados",
      errorMsg: "Error al guardar colores del calendario",
    }),

  /** GET /config-options/general-settings — lectura abierta a autenticados.
   *  Lo consulta el calendario para decidir si pide confirmación al fijar un
   *  precio por noche bajo el umbral. `silent` evita el toast de error si el
   *  backend aún no tiene el endpoint (degrada al default del frontend). */
  getGeneralSettings: (): Promise<GeneralSettingsResponse | null> =>
    apiFetch<GeneralSettingsResponse>("/config-options/general-settings", {
      silent: true,
    }),

  /** PUT /config-options/general-settings — admin only. */
  updateGeneralSettings: (
    body: Partial<GeneralSettings>,
  ): Promise<GeneralSettingsResponse | null> =>
    apiFetch<GeneralSettingsResponse>("/config-options/general-settings", {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Ajustes generales guardados",
      errorMsg: "Error al guardar los ajustes generales",
    }),
};
